import { IonContent, IonImg } from '@ionic/react';
import React from 'react';

import { UiButtonBase } from '@/components/ui/ButtonBase';
import { UiTypography } from '@/components/ui/Typography';
import { S3_BUCKET_URL } from '@/constants';
import { useAccount } from '@/hooks/useAccount';
import { useAppModals } from '@/hooks/useAppModals';
import { useStripeV2Plans } from '@/hooks/useStripeV2Plans';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { SectionPaymentMethodSelection } from '@/pages/Paywall/sections/PaymentMethodSelection/PaymentMethodSelection';
import { StripeV2CoinsPrice } from '@/services/stripe/dto';

import {
  ActionButtonsStyles,
  BannerContentPriceStyles,
  BannerContentStyles,
  BannerImageStyles,
  BannerStyles,
  CoinsPaywallModalStyles,
  ImageStyles,
  OneTimeOfferBadgeStyles,
  SubtitleStyles,
  TitleStyles,
} from './CoinsPaywall.styles';
import { getCoinsUpsaleImage } from './utils/getCoinsUpsaleImage';

export const CoinsPaywallModal: React.FC = () => {
  const { hide, isVisible } = useAppModals('coinsPaywall');
  const { user, setSelectedCoinPlanId } = useAccount();
  const { coinsPlans } = useStripeV2Plans();

  const [showPaymentElements, setShowPaymentElements] = React.useState(false);

  const gender = user?.onboarding_data.gender || 'female';

  const handleProductSelect = (coinPlan: StripeV2CoinsPrice) => {
    setSelectedCoinPlanId(coinPlan.id);
    setShowPaymentElements(true);
  };

  const handleDeclineOffer = () => {
    setSelectedCoinPlanId(null); // Clear any selected coins
    setShowPaymentElements(true);
  };

  const handlePresent = () => {
    setShowPaymentElements(false);
    amplitudeModule.logEvent('onboarding_screen', {
      seen: 'CoinsPaywall',
    });
  };

  return (
    <CoinsPaywallModalStyles
      key="coins-paywall-modal"
      id="coins-paywall-modal"
      role="dialog"
      breakpoints={[0, 1]}
      initialBreakpoint={1}
      isOpen={isVisible}
      onWillDismiss={hide}
      onWillPresent={handlePresent}
      staticTheme={webCampaign.getStaticTheme()}
    >
      <IonContent>
        {!showPaymentElements ? (
          <CoinsUpsaleContent
            coinPlan={coinsPlans?.[0] || null}
            gender={gender}
            onProductSelect={handleProductSelect}
            onDeclineOffer={handleDeclineOffer}
          />
        ) : (
          <SectionPaymentMethodSelection />
        )}
      </IonContent>
    </CoinsPaywallModalStyles>
  );
};

function CoinsUpsaleContent({
  coinPlan,
  gender,
  onProductSelect,
  onDeclineOffer,
}: {
  coinPlan: StripeV2CoinsPrice | null;
  gender: string;
  onProductSelect: (coinPlan: StripeV2CoinsPrice) => void;
  onDeclineOffer: () => void;
}) {
  return (
    <>
      <ImageStyles>
        <IonImg src={getCoinsUpsaleImage(gender)} />
      </ImageStyles>
      <TitleStyles align="center" variant="title-medium" component="h1">
        Wow! you've just won a prize!!{' '}
        <span style={{ WebkitTextFillColor: 'initial' }}>🎉</span>
      </TitleStyles>
      <SubtitleStyles align="center" variant="body-medium">
        {gender === 'male'
          ? `Get 2 sexy outfits for your best-matched guy for just ${coinPlan?.label}!`
          : `Get 2 sexy outfits for your best-matched lady for just ${coinPlan?.label}!`}
      </SubtitleStyles>
      <BannerStyles>
        <BannerImageStyles
          src={`${S3_BUCKET_URL}/icons/coins-amount-huge.png`}
        />

        <BannerContentStyles className="banner-content">
          <OneTimeOfferBadgeStyles variant="label-small">
            One-time offer
          </OneTimeOfferBadgeStyles>
          <UiTypography variant="title-large" transform="bold">
            {coinPlan?.coins} Coins
          </UiTypography>
          <BannerContentPriceStyles className="banner-content-price">
            <UiTypography
              variant="body-large"
              decoration="line-through"
              color="neutral-white-static"
            >
              ${coinPlan?.original_price}
            </UiTypography>
            <UiTypography
              variant="title-medium"
              color="secondary-static"
              transform="bold"
            >
              ${coinPlan?.price}
            </UiTypography>
          </BannerContentPriceStyles>
        </BannerContentStyles>
      </BannerStyles>
      <ActionButtonsStyles>
        <UiButtonBase
          size="large"
          expand="full"
          onClick={() => {
            if (coinPlan) {
              onProductSelect(coinPlan);
            }
          }}
        >
          Grab the offer!
        </UiButtonBase>
        <UiButtonBase
          size="large"
          expand="full"
          variant="text"
          onClick={onDeclineOffer}
        >
          Decline the offer
        </UiButtonBase>
      </ActionButtonsStyles>
    </>
  );
}
